// client/src/components/COMMERCANT/MerchantProcessPaiement.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { storage, db } from '../../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import { sanitize } from '../../utils/sanitize';
import LegalAccordion from '../shared/LegalAccordion';
import { CURRENCY_GROUPS, PRICING } from '../../utils/merchantPricing';
import { logoutSafely } from '../../utils/logoutSafely';
import { FaLock, FaClock, FaCheck, FaEnvelope, FaSignOutAlt } from 'react-icons/fa';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const RAW_API =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
  (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5099');
const API_BASE = RAW_API.replace(/\/+$/, '');

const normalizePeriod = (raw) => {
  if (!raw) return null;
  const val = String(raw).toLowerCase();
  if (val.startsWith('ann')) return 'annual';
  if (val.startsWith('men') || val.startsWith('mon')) return 'monthly';
  if (val === 'annual' || val === 'monthly') return val;
  return null;
};

// Mapping territoire → code ISO 2 lettres (pour Stripe billing address)
const TERRITORY_ISO = {
  'France (métropole)': 'FR', 'Guadeloupe': 'GP', 'Martinique': 'MQ',
  'Guyane française': 'GF', 'La Réunion': 'RE', 'Mayotte': 'YT',
  'Saint-Martin (FR)': 'MF', 'Saint-Barthélemy': 'BL', 'Saint-Pierre-et-Miquelon': 'PM',
  'Allemagne': 'DE', 'Autriche': 'AT', 'Belgique': 'BE', 'Croatie': 'HR',
  'Chypre': 'CY', 'Estonie': 'EE', 'Finlande': 'FI', 'Grèce': 'GR',
  'Irlande': 'IE', 'Italie': 'IT', 'Lettonie': 'LV', 'Lituanie': 'LT',
  'Luxembourg': 'LU', 'Malte': 'MT', 'Pays-Bas': 'NL', 'Portugal': 'PT',
  'Açores': 'PT', 'Madère': 'PT', 'Espagne': 'ES', 'Îles Canaries': 'ES',
  'Ceuta': 'ES', 'Melilla': 'ES', 'Slovaquie': 'SK', 'Slovénie': 'SI',
  'Monaco': 'MC', 'Saint-Marin': 'SM', 'Vatican': 'VA', 'Andorre': 'AD',
  'Kosovo': 'XK', 'Monténégro': 'ME', 'Îles Åland': 'FI',
  'États-Unis': 'US', 'Porto Rico': 'PR', 'Îles Vierges américaines': 'VI',
  'Guam': 'GU', 'Samoa américaines': 'AS', 'Îles Mariannes du Nord': 'MP',
  'Panama': 'PA', 'Équateur': 'EC', 'Salvador': 'SV', 'Timor oriental': 'TL',
  'Îles Turques-et-Caïques': 'TC', 'Îles Vierges britanniques': 'VG',
  'Bonaire, Saint-Eustache et Saba': 'BQ', 'Îles Marshall': 'MH',
  'Micronésie': 'FM', 'Palaos': 'PW', 'Sint Maarten (NL)': 'SX',
  'Saint-Barthélemy (USD)': 'BL', 'Zimbabwe': 'ZW',
  'Canada': 'CA',
  'Royaume-Uni': 'GB', 'Angleterre': 'GB', 'Écosse': 'GB',
  'Pays de Galles': 'GB', 'Irlande du Nord': 'GB', 'Gibraltar': 'GI',
  'Île de Man': 'IM', 'Jersey': 'JE', 'Guernesey': 'GG',
  'Suisse': 'CH', 'Liechtenstein': 'LI',
  'Bénin': 'BJ', 'Burkina Faso': 'BF', "Côte d'Ivoire": 'CI',
  'Guinée-Bissau': 'GW', 'Mali': 'ML', 'Niger': 'NE', 'Sénégal': 'SN', 'Togo': 'TG',
  'Cameroun': 'CM', 'Centrafrique': 'CF', 'Tchad': 'TD',
  'Congo (République du)': 'CG', 'Guinée équatoriale': 'GQ', 'Gabon': 'GA',
};

const SERVICES = [
  'Forfait base',
  'Programme de fidélité',
  'Communication intégrée',
  'Stockage factures & documents',
];

// ─── Formulaire de paiement Stripe ───────────────────────────────────────────
const CheckoutForm = ({ onSuccess, onBack, onPaymentMethodReady, planLabel, priceLabel, billingDetails, countryCode, promoDetails, promoAfterLabel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true);
    setPayError(null);
    const fullBillingDetails = {
      name: billingDetails.name,
      email: billingDetails.email,
      phone: billingDetails.phone,
      address: {
        country: countryCode || '',
        line1: billingDetails.line1 || '',
        line2: '',
        city: billingDetails.city || '',
        postal_code: billingDetails.postal_code || '',
        state: '',
      },
    };
    // Étape 1 : confirmer le SetupIntent (sauvegarde CB, aucune facture créée)
    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: { billing_details: fullBillingDetails },
      },
      redirect: 'if_required',
    });
    if (error) {
      setPayError(error.message);
      setPaying(false);
      return;
    }
    // Étape 2 : créer l'abonnement côté serveur → facture créée et payée
    let subscriptionResult;
    try {
      subscriptionResult = await onPaymentMethodReady(setupIntent.payment_method);
    } catch (err) {
      setPayError(err.message || "Erreur lors de la création de l'abonnement");
      setPaying(false);
      return;
    }

    // Étape 3 : si la banque exige une 3DS sur le prélèvement (cas rare SCA Europe),
    // on déclenche la confirmation ici avant d'afficher le succès
    if (subscriptionResult?.paymentIntentClientSecret) {
      const { error: confirmError } = await stripe.confirmCardPayment(
        subscriptionResult.paymentIntentClientSecret
      );
      if (confirmError) {
        setPayError(confirmError.message);
        setPaying(false);
        return;
      }
    }

    onSuccess();
    setPaying(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div className={`checkout-summary${promoDetails ? ' checkout-summary--promo' : ''}`}>
        <div className="checkout-summary-main">
          <span className="checkout-plan">{planLabel}</span>
          <span className="checkout-price">{priceLabel}</span>
        </div>
        {promoDetails && (
          <div className="checkout-promo-row">
            <span className="checkout-promo-pill">-{promoDetails.percentage}%</span>
            <div className="checkout-promo-text">
              <span>Code partenaire appliqué : -{promoDetails.percentage}%</span>
              {promoAfterLabel && <span className="checkout-promo-sub">{promoAfterLabel}</span>}
            </div>
          </div>
        )}
      </div>

      <div style={{ margin: '20px 0' }}>
        <PaymentElement options={{
          layout: 'tabs',
          fields: {
            billingDetails: {
              name: 'never',
              email: 'never',
              phone: 'never',
              address: 'never',
            },
          },
          defaultValues: {
            billingDetails: {
              address: { country: countryCode || undefined },
            },
          },
          terms: {
            card: 'never',
          },
          wallets: {
            link: 'never',
          },
        }} />
      </div>

      {payError && <div className="pay-error">{payError}</div>}

      <button type="submit" disabled={!stripe || paying} className="btn btn-primary pay-submit-btn">
        {paying ? 'Paiement en cours…' : 'Confirmer le paiement'}
      </button>
      <button type="button" onClick={onBack} disabled={paying} className="btn back-btn">
        ← Retour
      </button>
    </form>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────
const MerchantProcessPaiement = () => {
  const [logoUrl, setLogoUrl] = useState(null);

  // Champs modale
  const [newPrenom, setNewPrenom] = useState('');
  const [newNom, setNewNom] = useState('');
  const [newPseudo, setNewPseudo] = useState('');
  const [newTelephone, setNewTelephone] = useState('');
  const [newRue, setNewRue] = useState('');
  const [newVille, setNewVille] = useState('');
  const [newCodePostal, setNewCodePostal] = useState('');
  const [modalLieu, setModalLieu] = useState('');
  const [acceptedCGUV, setAcceptedCGUV] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Données utilisateur confirmées
  const [confirmedLieu, setConfirmedLieu] = useState('');
  // eslint-disable-next-line
  const [promoCode, setPromoCode] = useState(null);
  const [promoDetails, setPromoDetails] = useState(null); // { percentage, monthNumber }
  const [uidFromState, setUidFromState] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState({});

  // Étapes : 'pricing' → 'payment' → 'success'
  const [step, setStep] = useState('pricing');
  const [clientSecret, setClientSecret] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [autoStartFired, setAutoStartFired] = useState(false);

  const location = useLocation();
  const { code: rawCode, uid: rawUid } = location.state || {};
  const storedAutoStart = (() => { try { return localStorage.getItem('merchant_autostart') === 'true'; } catch { return false; } })();
  const autoStart = !!location.state?.autoStart || storedAutoStart;
  const storedPromo = (() => { try { return sessionStorage.getItem('merchant_preselected_promo') || ''; } catch { return ''; } })();

  const planFromState = normalizePeriod(location.state?.selectedPlan);
  const readStoredPlan = () => { try { return normalizePeriod(localStorage.getItem('merchant_preselected_plan')); } catch { return null; } };
  const [preferredPlan, setPreferredPlan] = useState(() => planFromState || readStoredPlan()); // eslint-disable-line

  const ALL_TERRITORIES = useMemo(() => {
    return CURRENCY_GROUPS
      .flatMap(g => g.territories.map(t => ({ ...t })))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  }, []);

  useEffect(() => { if (rawUid) setUidFromState(sanitize(rawUid)); }, [rawUid]);

  // Fetch détails code promo (sans auth, endpoint public)
  useEffect(() => {
    const code = sanitize(rawCode || '') || storedPromo;
    if (!code) { setPromoDetails(null); return; }
    fetch(`${API_BASE}/api/promo-codes/details?code=${encodeURIComponent(code)}`)
      .then(r => r.json())
      .then(data => {
        if (data.found) setPromoDetails({ percentage: data.percentage, monthNumber: data.monthNumber });
        else setPromoDetails(null);
      })
      .catch(() => setPromoDetails(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pré-remplir confirmedLieu depuis le state de navigation (parcours affilié)
  // pour déclencher autoStart dès le premier render sans attendre Firestore
  useEffect(() => {
    const t = location.state?.selectedTerritory;
    if (t) setConfirmedLieu(sanitize(t));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = 'white';
    const logoRef = ref(storage, 'Administratif/logo_ecobill_ok.png');
    getDownloadURL(logoRef).then(url => setLogoUrl(url)).catch(() => {});
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => setCurrentUser(user || null));
    return () => unsub();
  }, []);


  useEffect(() => {
    const userUid = uidFromState || (currentUser && currentUser.uid);
    const code = sanitize(rawCode || '');
    const codeToSave = code || storedPromo;
    if (userUid && codeToSave) {
      updateDoc(doc(db, 'users', userUid), { codepromo: codeToSave }).catch(() => {});
    }
  }, [currentUser, uidFromState, rawCode, storedPromo]);

  useEffect(() => {
    const userUid = uidFromState || (currentUser && currentUser.uid);
    if (!userUid) return;
    const unsubDoc = onSnapshot(doc(db, 'users', userUid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserData(data);
        setShowModal(!data.pseudo);
        const savedLieu = data.lieu ? sanitize(data.lieu) : '';
        if (savedLieu) setConfirmedLieu(savedLieu);
        setPromoCode(data.codepromo ? sanitize(data.codepromo) : null);
        // Récupérer le forfait choisi depuis Firestore (persiste même en nouvel onglet post-vérification email)
        if (data.pendingPlan && !planFromState && !readStoredPlan()) {
          setPreferredPlan(normalizePeriod(data.pendingPlan));
        }
      }
    }, () => {});
    return () => unsubDoc();
    // eslint-disable-next-line
  }, [currentUser, uidFromState]);

  const getSelectedCurrencyCode = useCallback(() => {
    const t = ALL_TERRITORIES.find(t => t.value === sanitize(confirmedLieu));
    return t?.currencyCode || 'EUR';
  }, [ALL_TERRITORIES, confirmedLieu]);

  const getPlanPrices = useCallback(() => {
    return PRICING[getSelectedCurrencyCode()] || PRICING.DEFAULT;
  }, [getSelectedCurrencyCode]);

  const handleSaveModal = async () => {
    const auth = getAuth();
    const user = currentUser || auth.currentUser;
    const safePrenom   = sanitize(newPrenom);
    const safeNom      = sanitize(newNom);
    const safePseudo   = sanitize(newPseudo);
    const safeTel      = sanitize(newTelephone);
    const safeAdresse  = [sanitize(newRue), sanitize(newCodePostal), sanitize(newVille)].filter(Boolean).join(', ');
    const safeLieu     = sanitize(modalLieu);
    const safeCurrency = ALL_TERRITORIES.find(t => t.value === safeLieu)?.currencyCode || null;

    if (!user || !safePrenom.trim() || !safeNom.trim() || !safePseudo.trim() || !safeTel.trim() || !sanitize(newRue).trim() || !sanitize(newVille).trim() || !safeLieu || !acceptedCGUV) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        prenom: safePrenom,
        nom: safeNom,
        pseudo: safePseudo,
        telephone: safeTel,
        adresse: safeAdresse,
        rue: sanitize(newRue),
        ville: sanitize(newVille),
        codePostal: sanitize(newCodePostal),
        lieu: safeLieu,
        CGV: 'accepted',
        currencyCode: safeCurrency,
      });
      setConfirmedLieu(safeLieu);
      setShowModal(false);
    } catch {}
  };

  // Auto-démarrage depuis le parcours affilié (MerchantOfferSelection step 2)
  useEffect(() => {
    if (!autoStart || autoStartFired || !confirmedLieu || showModal || step !== 'pricing' || subscriptionLoading) return;
    setAutoStartFired(true);
    try { localStorage.removeItem('merchant_autostart'); } catch {}
    // Lire le plan directement depuis location.state pour éviter toute valeur stale
    const plan = normalizePeriod(location.state?.selectedPlan) || preferredPlan || 'monthly';
    handleChoosePlan(plan);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, autoStartFired, confirmedLieu, showModal, step, subscriptionLoading]);

  const handleChoosePlan = async (period) => {
    if (!confirmedLieu || showModal) return;
    const normalized = normalizePeriod(period) || 'monthly';
    setSelectedPeriod(normalized);
    setSubscriptionLoading(true);
    setSubscriptionError(null);
    try {
      const auth = getAuth();
      const user = currentUser || auth.currentUser;
      const idToken = await user.getIdToken();
      const currency = getSelectedCurrencyCode();
      const resp = await fetch(`${API_BASE}/api/create-setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({
          currency,
          period: normalized,
          territory: confirmedLieu,
          countryCode: TERRITORY_ISO[confirmedLieu] || '',
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Erreur serveur');
      }
      const { clientSecret: cs } = await resp.json();
      setClientSecret(cs);
      setStep('payment');
    } catch (err) {
      setSubscriptionError(err.message || "Erreur lors de la création de l'abonnement");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handlePaymentMethodReady = async (paymentMethodId) => {
    const auth = getAuth();
    const user = currentUser || auth.currentUser;
    const idToken = await user.getIdToken();
    const currency = getSelectedCurrencyCode();
    const effectivePromo = sanitize(rawCode || '') || storedPromo || '';

    const resp = await fetch(`${API_BASE}/api/create-subscription-with-pm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({
        paymentMethodId,
        currency,
        period: selectedPeriod,
        territory: confirmedLieu,
        countryCode: TERRITORY_ISO[confirmedLieu] || '',
        promoCode: effectivePromo || undefined,
      }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || 'Erreur lors de la création de l\'abonnement');
    }
    return await resp.json(); // { subscriptionId, status, paymentIntentClientSecret }
  };

  const handleLogout = async () => {
    const auth = getAuth();
    try { await logoutSafely(auth); } catch {}
    try { localStorage.removeItem('userEmail'); localStorage.removeItem('userRole'); } catch {}
    window.location.href = '/';
  };

  const isFormValid = newPrenom.trim() && newNom.trim() && newPseudo.trim() && newTelephone.trim() && newRue.trim() && newVille.trim() && modalLieu && acceptedCGUV;

  const prices = getPlanPrices();
  const applyDiscount = (amount) => promoDetails
    ? amount * (1 - promoDetails.percentage / 100)
    : amount;
  const fmt = (n) => { const v = Number(n); return v % 1 === 0 ? String(v) : v.toFixed(2); };

  const displayMonthly        = `${fmt(applyDiscount(prices.monthly))}${prices.currency}`;
  const displayMonthlyOrig    = `${prices.monthly}${prices.currency}`;
  const displayAnnualTotal    = `${fmt(applyDiscount(prices.annual))}${prices.currency}`;
  const displayAnnualOrigTotal = `${prices.annual}${prices.currency}`;
  const displayAnnualPerMonth = `${fmt(applyDiscount(prices.annual) / 12)}${prices.currency}`;
  const planButtonsDisabled   = showModal || !confirmedLieu || subscriptionLoading;

  const getPromoAfterLabel = () => {
    if (!promoDetails) return null;
    if (selectedPeriod === 'annual') return `Remisé pendant 1 an · puis ${displayAnnualOrigTotal}/an`;
    if (promoDetails.monthNumber > 0) return `Remisé pendant ${promoDetails.monthNumber} mois · puis ${displayMonthlyOrig}/mois`;
    return null;
  };

  const getPlanLabel  = () => selectedPeriod === 'annual' ? 'Abonnement Annuel' : 'Abonnement Mensuel';
  const getPriceLabel = () => selectedPeriod === 'annual'
    ? `${displayAnnualTotal}/an`
    : `${displayMonthly}/mois`;

  const countryCode = TERRITORY_ISO[confirmedLieu] || undefined;

  const billingDetails = {
    name:  [userData.prenom, userData.nom].filter(Boolean).join(' ') || userData.pseudo || '',
    email: currentUser?.email || '',
    phone: userData.telephone || '',
    line1: userData.rue || '',
    city:  userData.ville || '',
    postal_code: userData.codePostal || '',
  };

  const stripeOptions = clientSecret ? {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: { colorPrimary: '#274C74', borderRadius: '10px', fontFamily: 'Inter, sans-serif' },
    },
  } : null;

  const confirmedTerritoryLabel = ALL_TERRITORIES.find(t => t.value === confirmedLieu)?.label || confirmedLieu;

  return (
    <>
      <style>{CSS}</style>

      {/* ── Modale infos entreprise ── */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-card" role="dialog" aria-modal="true">
            <div className="modal-header">
              {logoUrl && <img src={logoUrl} alt="Logo" style={{ maxWidth: 100, marginBottom: 4 }} />}
              <h2 className="modal-title">Informations de votre entreprise</h2>
              <p className="modal-hint">Ces informations seront utilisées pour votre compte et vos factures.</p>
            </div>

            {/* Contact */}
            <div className="modal-section-label">Contact de référence</div>
            <div className="modal-row-2">
              <input
                type="text" value={newPrenom}
                onChange={e => setNewPrenom(e.target.value)}
                placeholder="Prénom"
              />
              <input
                type="text" value={newNom}
                onChange={e => setNewNom(e.target.value)}
                placeholder="Nom"
              />
            </div>

            {/* Entreprise */}
            <div className="modal-section-label">Entreprise</div>
            <input
              type="text" value={newPseudo}
              onChange={e => setNewPseudo(e.target.value)}
              placeholder="Nom de l'entreprise"
            />
            <input
              type="text" value={newTelephone}
              onChange={e => setNewTelephone(e.target.value)}
              placeholder="Téléphone"
            />
            {/* Adresse + Territoire — un seul bloc */}
            <div className="modal-address-block">
              <div className="modal-section-label" style={{ marginBottom: 10 }}>Adresse de l'entreprise</div>
              <input
                type="text" value={newRue}
                onChange={e => setNewRue(e.target.value)}
                placeholder="Adresse"
                style={{ marginBottom: 10 }}
              />
              <div className="modal-row-2" style={{ marginBottom: 10 }}>
                <input
                  type="text" value={newVille}
                  onChange={e => setNewVille(e.target.value)}
                  placeholder="Ville"
                  style={{ marginBottom: 0 }}
                />
                <input
                  type="text" value={newCodePostal}
                  onChange={e => setNewCodePostal(e.target.value)}
                  placeholder="Code postal"
                  style={{ marginBottom: 0 }}
                />
              </div>
              <select
                value={modalLieu}
                onChange={e => setModalLieu(sanitize(e.target.value))}
                className="modal-territory-select"
              >
                <option value="" disabled>— Sélectionnez —</option>
                {ALL_TERRITORIES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <div className="modal-territory-warn">
                Le pays détermine votre devise monétaire et ne pourra pas être modifié ultérieurement.
              </div>
            </div>

            {/* CGV */}
            <LegalAccordion docId="cgv" checked={acceptedCGUV} onChange={setAcceptedCGUV} />

            <button className="merchant-button" onClick={handleSaveModal} disabled={!isFormValid}>
              Enregistrer et continuer →
            </button>
          </div>
        </div>
      )}

      <div className="page-wrap">
        {logoUrl && <img src={logoUrl} alt="Logo" className="page-logo" />}

        {/* ── Étape 1 : Choix du plan ── */}
        {step === 'pricing' && autoStart && (!autoStartFired || subscriptionLoading) && (
          <div className="page-content page-loading" role="status">
            <span className="eb-spinner" aria-hidden="true" />
            <p>Préparation de votre abonnement…</p>
          </div>
        )}
        {step === 'pricing' && (!autoStart || (autoStartFired && !subscriptionLoading)) && (
          <div className={`page-content ${showModal ? 'blur-mask' : ''}`}>
            <h2 className="page-title">Choisissez votre formule</h2>
            <p className="page-subtitle">
              {confirmedTerritoryLabel && `${confirmedTerritoryLabel} · `}Tout inclus, sans frais cachés
            </p>

            <div className="activation-notice">
              <FaClock aria-hidden="true" /> Le compte sera activé 24-48h après le paiement
            </div>

            {subscriptionError && (
              <div className="pay-error" role="alert" style={{ maxWidth: 520, margin: '0 auto 16px' }}>{subscriptionError}</div>
            )}

            <div className="plan-grid">
              {/* Mensuel */}
              <div className="plan-card">
                {promoDetails && <div className="promo-badge">-{promoDetails.percentage}% partenaire</div>}
                <div className="plan-title">Mensuel</div>
                <div className="plan-sub">Idéal pour démarrer, sans engagement</div>
                <div className="plan-price">
                  <span className="price-amount">{displayMonthly}</span>
                  <span className="price-period"> / mois</span>
                </div>
                {promoDetails && (
                  <>
                    {promoDetails.monthNumber > 0 && (
                      <div className="promo-duration">Remisé pendant {promoDetails.monthNumber} mois</div>
                    )}
                    <div className="promo-then">Puis {displayMonthlyOrig}/mois</div>
                  </>
                )}
                <div className="plan-features">
                  <ul>
                    {SERVICES.map(s => <li key={s}>{s}</li>)}
                    <li>Support prioritaire 7/7</li>
                    <li>Sans engagement</li>
                  </ul>
                </div>
                <div className="plan-cta">
                  <button className="btn" onClick={() => handleChoosePlan('monthly')} disabled={planButtonsDisabled}>
                    {subscriptionLoading && selectedPeriod === 'monthly' ? 'Chargement…' : 'Choisir ce forfait'}
                  </button>
                </div>
              </div>

              {/* Annuel */}
              <div className="plan-card plan-card--featured">
                {promoDetails
                  ? <div className="plan-badge">-{promoDetails.percentage}% partenaire 🎁</div>
                  : <div className="plan-badge">1 mois offert 🎁</div>
                }
                <div className="plan-title">Annuel</div>
                <div className="plan-sub">1 mois offert pour un engagement de 12 mois</div>
                <div className="plan-price">
                  <span className="price-amount">{displayAnnualTotal}</span>
                  <span className="price-period"> / an</span>
                  <span className="plan-equiv">(soit {displayAnnualPerMonth}/mois)</span>
                </div>
                {promoDetails
                  ? (
                    <>
                      <div className="promo-duration">Remisé pendant 1 an</div>
                      <div className="promo-then">Puis {displayAnnualOrigTotal}/an</div>
                    </>
                  )
                  : <div className="plan-total">Total annuel : {displayAnnualTotal}</div>
                }
                <div className="plan-features">
                  <ul>
                    {SERVICES.map(s => <li key={s}>{s}</li>)}
                    <li>Support prioritaire 7/7</li>
                    <li>Engagement de 1 an</li>
                  </ul>
                </div>
                <div className="plan-cta">
                  <button className="btn btn-primary" onClick={() => handleChoosePlan('annual')} disabled={planButtonsDisabled}>
                    {subscriptionLoading && selectedPeriod === 'annual' ? 'Chargement…' : 'Choisir ce forfait'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Étape 2 : Paiement Stripe ── */}
        {step === 'payment' && clientSecret && stripeOptions && (
          <div className="page-content">
            <h2 className="page-title">Paiement sécurisé</h2>
            <p className="page-subtitle">Vos données sont chiffrées et sécurisées par Stripe</p>
            <div className="payment-wrap">
              <Elements stripe={stripePromise} options={stripeOptions}>
                <CheckoutForm
                  onSuccess={() => setStep('success')}
                  onBack={() => { setStep('pricing'); setClientSecret(null); setSubscriptionError(null); }}
                  onPaymentMethodReady={handlePaymentMethodReady}
                  planLabel={getPlanLabel()}
                  priceLabel={getPriceLabel()}
                  billingDetails={billingDetails}
                  countryCode={countryCode}
                  promoDetails={promoDetails}
                  promoAfterLabel={getPromoAfterLabel()}
                />
              </Elements>
              <div className="payment-secure"><FaLock aria-hidden="true" /> Paiement chiffré, traité par Stripe</div>
            </div>
          </div>
        )}

        {/* ── Étape 3 : Succès ── */}
        {step === 'success' && (
          <div className="page-content success-wrap">
            <div className="success-icon"><FaCheck aria-hidden="true" /></div>
            <h2 className="page-title">Paiement reçu !</h2>
            <p className="page-subtitle" style={{ maxWidth: 400, textAlign: 'center', lineHeight: 1.6 }}>
              Merci pour votre abonnement. Votre compte sera activé <strong>sous 24-48h</strong>.<br />
              Vous recevrez un email de confirmation dès l'activation.
            </p>
            <div className="activation-notice">
              <FaEnvelope aria-hidden="true" /> Un reçu vous a été envoyé par email
            </div>
          </div>
        )}

        <button className="logout-btn" onClick={handleLogout}><FaSignOutAlt aria-hidden="true" /> Déconnexion</button>
      </div>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const CSS = `
  .page-wrap {
    min-height: 100vh; min-height: 100dvh;
    background:
      radial-gradient(900px 500px at 15% -10%, rgba(39, 76, 116, 0.12), transparent 60%),
      radial-gradient(700px 400px at 100% 110%, rgba(20, 48, 40, 0.08), transparent 60%),
      var(--eb-bg);
    display: flex; flex-direction: column; align-items: center;
    justify-content: flex-start; padding: 40px 16px 64px;
    color: var(--eb-text);
  }
  .page-logo { max-width: 88px; height: auto; margin-bottom: 24px; }
  .page-content { width: 100%; max-width: 860px; }
  .blur-mask { filter: blur(2px); pointer-events: none; user-select: none; }
  .page-title { text-align: center; font-size: clamp(24px, 3.5vw, 32px); font-weight: 800; letter-spacing: -0.025em; color: var(--eb-text); margin: 0 0 6px; }
  .page-subtitle { text-align: center; color: var(--eb-muted); margin: 0 0 24px; font-size: 15px; }
  .page-loading { display: flex; flex-direction: column; align-items: center; padding-top: 60px; gap: 16px; }
  .page-loading p { margin: 0; color: var(--eb-muted); font-weight: 500; font-size: 15px; }

  /* Notice */
  .activation-notice {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    max-width: 520px; margin: 0 auto 28px; padding: 10px 14px; text-align: center;
    background: var(--eb-info-bg); border: 1px solid #cfe0f7; border-radius: var(--eb-radius-sm);
    font-size: 14px; font-weight: 500; color: #1b4a80;
  }

  /* Grille des formules */
  .plan-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: stretch; }
  .plan-card {
    position: relative; border-radius: var(--eb-radius-xl); padding: 28px 24px 24px; background: var(--eb-surface);
    border: 1px solid var(--eb-border); display: flex; flex-direction: column;
    box-shadow: var(--eb-shadow-sm); transition: box-shadow .2s ease, border-color .2s ease;
  }
  .plan-card:hover { box-shadow: var(--eb-shadow-md); border-color: var(--eb-border-strong); }
  .plan-card--featured, .plan-card--featured:hover { border: 2px solid var(--eb-brand); box-shadow: var(--eb-shadow-md); }
  .plan-badge, .promo-badge {
    position: absolute; top: -13px; left: 50%; transform: translateX(-50%);
    background: var(--eb-brand); color: #fff; font-size: 12.5px; font-weight: 600;
    padding: 4px 12px; border-radius: var(--eb-radius-pill); white-space: nowrap; box-shadow: var(--eb-shadow-xs);
  }
  .promo-badge { background: var(--eb-warning); }
  .promo-duration { text-align: center; font-size: 13px; font-weight: 600; color: var(--eb-warning); margin-top: 4px; margin-bottom: 2px; }
  .promo-then { text-align: center; font-size: 13.5px; color: var(--eb-text-2); font-weight: 500; margin-bottom: 6px; }
  .plan-equiv { display: block; color: var(--eb-muted); font-size: 13.5px; font-weight: 500; margin-top: 4px; }
  .plan-title { text-align: center; font-weight: 700; font-size: 20px; letter-spacing: -0.015em; color: var(--eb-text); }
  .plan-sub { text-align: center; color: var(--eb-muted); font-size: 14px; margin: 6px 0; min-height: 40px; }
  .plan-price { text-align: center; margin: 12px 0 4px; padding-bottom: 16px; border-bottom: 1px solid var(--eb-border); }
  .price-amount { font-size: 36px; font-weight: 800; letter-spacing: -0.03em; color: var(--eb-text); font-variant-numeric: tabular-nums; }
  .price-period { font-size: 14px; color: var(--eb-muted); }
  .plan-total { text-align: center; color: var(--eb-text-2); font-weight: 600; font-size: 14px; margin: 8px 0; }
  .plan-features { margin: 16px 0 4px; font-size: 14px; color: var(--eb-text-2); flex-grow: 1; }
  .plan-features ul { list-style: none; padding: 0; margin: 0; }
  .plan-features li { display: flex; align-items: flex-start; margin-bottom: 10px; line-height: 1.4; }
  .plan-features li::before {
    content: '✓'; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
    width: 18px; height: 18px; margin-right: 10px; margin-top: 1px; border-radius: 50%;
    background: var(--eb-success-bg); color: var(--eb-success); font-size: 11px; font-weight: 800;
  }
  .plan-cta { margin-top: 16px; }

  /* Boutons */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    min-height: 48px; padding: 0 18px; width: 100%;
    border: 1px solid var(--eb-border-strong); background: var(--eb-surface); color: var(--eb-text-2);
    border-radius: var(--eb-radius); font-weight: 600; font-size: 15px; cursor: pointer; box-shadow: var(--eb-shadow-xs);
    transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  }
  .btn:hover:not(:disabled) { background: var(--eb-surface-2); color: var(--eb-text); }
  .btn:focus-visible { outline: none; box-shadow: var(--eb-ring); }
  .btn:disabled { opacity: .55; cursor: not-allowed; }
  .btn-primary { background: var(--eb-brand); border-color: var(--eb-brand); color: #fff; }
  .btn-primary:hover:not(:disabled) { background: var(--eb-brand-700); border-color: var(--eb-brand-700); color: #fff; }

  /* Paiement */
  .payment-wrap {
    background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-xl);
    padding: 28px; box-shadow: var(--eb-shadow-lg); max-width: 520px; margin: 0 auto;
  }
  .payment-secure { display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 16px; color: var(--eb-muted); font-size: 13px; }
  .checkout-summary {
    background: var(--eb-brand-50); border: 1px solid var(--eb-brand-100); border-radius: var(--eb-radius);
    padding: 14px 16px; margin-bottom: 4px; display: flex; flex-direction: column;
  }
  .checkout-summary--promo { background: var(--eb-warning-bg); border-color: #fde3b0; }
  .checkout-summary-main { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
  .checkout-plan { font-weight: 600; color: var(--eb-brand-800); font-size: 14.5px; }
  .checkout-price { font-weight: 700; color: var(--eb-text); font-size: 17px; font-variant-numeric: tabular-nums; }
  .checkout-promo-row { display: flex; align-items: center; gap: 10px; margin-top: 10px; padding-top: 10px; border-top: 1px solid #fde3b0; }
  .checkout-promo-pill { background: var(--eb-warning); color: #fff; font-size: 13px; font-weight: 700; padding: 3px 10px; border-radius: var(--eb-radius-pill); white-space: nowrap; flex-shrink: 0; }
  .checkout-promo-text { font-size: 14px; color: var(--eb-warning); font-weight: 600; display: flex; flex-direction: column; gap: 2px; }
  .checkout-promo-sub { font-size: 13px; color: var(--eb-text-2); font-weight: 400; }
  .pay-submit-btn { margin-top: 4px; min-height: 50px; font-size: 15.5px; }
  .back-btn { margin-top: 8px; background: transparent; border-color: transparent; box-shadow: none; color: var(--eb-muted); }
  .back-btn:hover:not(:disabled) { background: var(--eb-surface-2); }
  .pay-error {
    color: #912018; background: var(--eb-danger-bg); border: 1px solid #fbd0cb;
    border-radius: var(--eb-radius-sm); padding: 10px 14px; margin-bottom: 12px; font-size: 14px;
  }

  /* Succès */
  .success-wrap {
    display: flex; flex-direction: column; align-items: center; max-width: 520px; margin: 0 auto;
    padding: 36px 28px 28px; background: var(--eb-surface); border: 1px solid var(--eb-border);
    border-radius: var(--eb-radius-xl); box-shadow: var(--eb-shadow-lg);
  }
  .success-icon {
    width: 64px; height: 64px; border-radius: 50%; margin-bottom: 18px;
    display: flex; align-items: center; justify-content: center;
    background: var(--eb-success-bg); color: var(--eb-success); font-size: 30px;
  }
  .success-wrap .activation-notice { margin-bottom: 0; }

  /* Modale */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(15, 35, 56, .55);
    backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center; z-index: 9999;
    padding: 16px; overflow-y: auto;
  }
  .modal-card {
    background: var(--eb-surface); border-radius: var(--eb-radius-xl); padding: 28px 24px 24px;
    max-width: 480px; width: 100%; box-shadow: var(--eb-shadow-lg); margin: auto;
  }
  .modal-header { text-align: center; margin-bottom: 20px; }
  .modal-title { font-size: 20px; font-weight: 700; letter-spacing: -0.015em; color: var(--eb-text); margin: 0 0 6px; text-align: center; }
  .modal-hint { font-size: 14px; color: var(--eb-muted); margin: 0; }

  .modal-section-label {
    font-size: 12.5px; font-weight: 600; color: var(--eb-muted);
    text-transform: uppercase; letter-spacing: .05em; margin-bottom: 8px; margin-top: 4px;
  }
  .modal-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
  .modal-row-2 > * { min-width: 0; }
  .modal-card input:not([type='checkbox']):not([type='radio']) {
    width: 100%; min-height: 44px; padding: 0 12px; margin-bottom: 12px; font-size: 15px; box-shadow: var(--eb-shadow-xs);
  }
  .modal-row-2 input:not([type='checkbox']) { margin-bottom: 0; }
  .modal-address-block {
    background: var(--eb-surface-2); border: 1px solid var(--eb-border); border-radius: var(--eb-radius);
    padding: 14px 16px; margin-bottom: 16px;
  }
  .modal-address-block input { margin-bottom: 0; }
  .modal-territory-select {
    width: 100%; min-height: 44px; padding: 0 12px; font-size: 15px; margin-bottom: 10px; cursor: pointer;
  }
  .modal-territory-warn {
    font-size: 13px; color: var(--eb-warning); background: var(--eb-warning-bg); border: 1px solid #fde3b0;
    border-radius: var(--eb-radius-sm); padding: 8px 10px; font-weight: 500; line-height: 1.45;
  }

  .checkbox-area { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 16px; font-size: 14px; color: var(--eb-text-2); }
  .checkbox-area input[type="checkbox"] { flex-shrink: 0; width: 16px; height: 16px; cursor: pointer; margin: 0; }
  .checkbox-area label { margin: 0; cursor: pointer; }
  .link-cguv { color: var(--eb-brand); font-weight: 600; text-decoration: underline; }
  .merchant-button {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    background: var(--eb-brand); color: #fff; border: 1px solid transparent; border-radius: var(--eb-radius);
    min-height: 50px; padding: 0 20px; font-weight: 600; cursor: pointer; width: 100%;
    font-size: 15.5px; box-shadow: var(--eb-shadow-xs); transition: background-color .15s ease;
  }
  .merchant-button:disabled { opacity: .55; cursor: not-allowed; }
  .merchant-button:hover:not(:disabled) { background: var(--eb-brand-700); }
  .merchant-button:focus-visible { outline: none; box-shadow: var(--eb-ring); }

  .logout-btn {
    margin-top: 36px; display: inline-flex; align-items: center; gap: 8px; min-height: 40px;
    background: var(--eb-surface); color: var(--eb-danger); border: 1px solid #f4c7c2;
    border-radius: var(--eb-radius-sm); padding: 0 16px; font-weight: 600; font-size: 14px; cursor: pointer;
    box-shadow: var(--eb-shadow-xs); transition: background-color .15s ease, border-color .15s ease;
  }
  .logout-btn:hover { background: var(--eb-danger-bg); border-color: var(--eb-danger); }

  @media (max-height: 700px), (max-width: 480px) {
    .modal-overlay { align-items: flex-start; padding: 12px 12px 40px; }
    .modal-card { padding: 22px 16px; border-radius: var(--eb-radius-lg); margin: 0 auto; }
    .modal-header { margin-bottom: 16px; }
    .modal-title { font-size: 18px; }
    .modal-address-block { padding: 12px; }
  }

  @media (max-width: 640px) {
    .page-wrap { padding: 28px 16px 56px; }
    .page-logo { margin-bottom: 20px; }
    .plan-grid { grid-template-columns: 1fr; gap: 24px; max-width: 440px; margin: 0 auto; }
    .plan-card { padding: 24px 18px 18px; }
    .plan-sub { min-height: 0; }
    .price-amount { font-size: 32px; }
    .payment-wrap { padding: 20px 16px; }
    .success-wrap { padding: 28px 18px 20px; }
    .modal-row-2 { gap: 8px; }
  }
`;

export default MerchantProcessPaiement;
